import React, { useMemo, useState } from "react";
import { POS_COLORS } from "../data/players";
import { colors, fonts, spacing, radius, fontSize, fontWeight } from "../data/theme";
import {
  USER_CONFIG_KEY, keeperRosterSize, keeperSeatOptions,
  searchKeeperPlayers, findKeeperPlayer, sanitizeKeepers,
} from "../utils/keepers";

// Keeper entry — declare this year's kept players before a mock draft.
//
// Deliberately NOT inside the expert panel. This is the feature people asked for
// by name on r/ff, twice; it should be findable without hunting. The empty state
// is one collapsed header, so a non-keeper user never notices it exists.
//
// A row IS a stored record. The search input's value is `record.name`, so there
// is no separate query state to keep in step with the blob, and a half-typed name
// is a real (invalid) row rather than something held off to the side. That is on
// purpose: validateKeepers can then see it and refuse to start, which is the
// behaviour the spec asks for — a keeper league that silently drops a keeper is
// worse than one that refuses to start.
//
// NO SLEEPER AUTO-FILL, on purpose. The import plumbing parses `is_keeper`, but
// that flag marks LAST year's keepers in draft history, not this year's
// declarations. Prefilling from it would present a stale guess as fact in the one
// place where being wrong is unrecoverable — the user would draft against it and
// not know. If it ever goes in, it must be labelled a guess and be one click to
// clear.

const ROW_GAP = spacing.sm;

function SeatSelect({ value, options, onChange }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      aria-label="Keeper's team"
      style={{
        width: "100%", padding: `${spacing.sm}px ${spacing.xs}px`,
        borderRadius: radius.sm, fontSize: fontSize.caption, fontFamily: "inherit",
        fontWeight: value === USER_CONFIG_KEY ? fontWeight.bold : fontWeight.medium,
        background: colors.bg.deep,
        // The user's own seat reads in the brand colour wherever it appears, so
        // "mine" and "an opponent's" are never a careful read of similar text.
        color: value === USER_CONFIG_KEY ? colors.brand.accent : colors.text.primary,
        border: `1px solid ${value === USER_CONFIG_KEY ? colors.border.brandLight : colors.border.subtle}`,
        cursor: "pointer", boxSizing: "border-box",
      }}
    >
      {/* The user's seat is starred and sits above a rule, so it reads as its own
          group rather than the first of twelve equivalent names. Under the
          canonical room an opponent can share the user's display name, and
          keeperSeatOptions gives that one the "(CPU)" suffix — the two are NOT
          interchangeable, so they must never look it. */}
      {options.map(o => [
        <option key={o.key} value={o.key}>{o.isUser ? `★ ${o.label}` : o.label}</option>,
        o.isUser && options.length > 1
          ? <option key={`${o.key}-rule`} disabled>{"─".repeat(18)}</option>
          : null,
      ])}
    </select>
  );
}

function KeeperRow({ row, seatOptions, onPatch, onMove, onRemove }) {
  const [focused, setFocused] = useState(false);
  const resolved = findKeeperPlayer(row.name);
  const suggestions = useMemo(
    () => (focused && !resolved ? searchKeeperPlayers(row.name) : []),
    [focused, resolved, row.name]
  );
  const nameTyped = (row.name || "").trim().length > 0;
  const bad = nameTyped && !resolved;

  const posColor = resolved ? (POS_COLORS[resolved.pos] || null) : null;

  return (
    // A row is now two or three lines tall, so the old single-line gap ran
    // adjacent keepers together. The larger bottom margin is what keeps "which
    // price belongs to which player" readable at a glance.
    <div style={{ marginBottom: spacing.lg }}>
      {/* SEAT FIRST, on its own line. The seat is the fact that frames the rest
          of the row — whose keeper this is — so it leads, and a full line means
          its label can never truncate. That matters more than it looks: "★ Your
          team (Mike)" versus "Mike (CPU)" is the only thing standing between a
          user and handing their own keeper to the CPU seat sharing their name. */}
      <div className="keeper-row-seat" style={{ marginBottom: ROW_GAP }}>
        <SeatSelect value={row.owner} options={seatOptions} onChange={onMove} />
      </div>

      {/* Then ONE line, reading left to right: name · pos · team · price · remove.
          `.mock-setup-wrapper` is capped at 420px, so this line has ~355px to
          work with on a 1707px desktop exactly as on a phone. Everything except
          the name is flexShrink: 0 — when the width runs out the PLAYER INPUT is
          the only thing that gives, and it ellipsizes. The pill, the team, the
          price and the × keep their pixels at every width. */}
      <div className="keeper-row" style={{ display: "flex", gap: ROW_GAP, alignItems: "center" }}>
        {/* Player — the input's value IS the stored name. `minWidth: 0` is what
            permits the shrink; without it flexbox refuses to go below the
            content's intrinsic width and the line overflows instead. */}
        <div className="keeper-row-player" style={{ flex: "1 1 auto", position: "relative", minWidth: 0 }}>
          <input
            type="text"
            value={row.name}
            placeholder="Player name..."
            aria-label="Kept player"
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 120)}
            onChange={e => {
              const name = e.target.value;
              // Set `pos` from the pool whenever the typed text is an exact
              // match, so picking from the list and typing it out land on the
              // same record. resolveKeepers trusts the pool's pos anyway; this
              // just keeps the stored copy honest.
              const p = findKeeperPlayer(name);
              onPatch({ name, pos: p ? p.pos : "" });
            }}
            style={{
              width: "100%", padding: `${spacing.sm}px ${spacing.md}px`,
              borderRadius: radius.sm, fontSize: fontSize.caption, fontFamily: "inherit",
              background: colors.bg.deep, color: colors.text.primary,
              border: `1px solid ${bad ? "rgba(239,68,68,0.55)" : colors.border.subtle}`,
              boxSizing: "border-box",
              // The designated loser when the line runs out of room. An input
              // clips rather than ellipsizes while focused, which is correct —
              // you want to see what you are typing — but a long resolved name
              // reads with an ellipsis at rest.
              textOverflow: "ellipsis",
            }}
          />
          {/* The resolved player's pos/team used to be an absolutely-positioned
              span INSIDE this wrapper, printed straight over the input's own text
              — the overlap Ben caught pre-push. It now lives on its own line
              below the row (see the meta line at the end of this component), so
              there is no surface for it to collide with at any width. */}
          {suggestions.length > 0 && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
              marginTop: 2, background: colors.bg.surface,
              border: `1px solid ${colors.border.subtle}`, borderRadius: radius.sm,
              boxShadow: "0 6px 20px rgba(0,0,0,0.45)", overflow: "hidden",
            }}>
              {suggestions.map(p => (
                <button
                  key={`${p.name}-${p.pos}`}
                  type="button"
                  // mousedown, not click: blur fires first and would close the
                  // list before a click ever landed.
                  onMouseDown={() => onPatch({ name: p.name, pos: p.pos })}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: `${spacing.sm}px ${spacing.md}px`,
                    background: "transparent", border: "none", cursor: "pointer",
                    fontSize: fontSize.caption, fontFamily: "inherit", color: colors.text.primary,
                  }}
                >
                  {/* Same pill idiom as the row's meta line and PlayerList, so
                      the position reads identically wherever it appears. */}
                  <span className="tag" style={{
                    background: (POS_COLORS[p.pos] || {}).bg || colors.bg.elevated,
                    color: (POS_COLORS[p.pos] || {}).text || colors.text.muted,
                    fontSize: 9, padding: "1px 5px", minWidth: 28, textAlign: "center",
                    fontWeight: fontWeight.bold, marginRight: spacing.xs, flexShrink: 0,
                  }}>{p.pos}</span>
                  {p.name}
                  <span style={{ color: colors.text.muted, marginLeft: spacing.xs, fontSize: fontSize.micro }}>
                    {p.team}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Position pill + team, INLINE on the player line now rather than on a
            line of their own. Same `.tag`/POS_COLORS idiom as PlayerList, so the
            position reads identically everywhere. Both are flexShrink: 0 and
            nowrap: they are two short tokens, and abbreviating an already
            abbreviated team code communicates nothing. */}
        {resolved && (
          <>
            <span className="tag" style={{
              background: posColor?.bg || colors.bg.elevated,
              color: posColor?.text || colors.text.muted,
              fontSize: 9, padding: "1px 5px", minWidth: 28, textAlign: "center",
              fontWeight: fontWeight.bold, flexShrink: 0,
            }}>{resolved.pos}</span>
            <span style={{
              fontSize: fontSize.micro, color: colors.text.muted,
              flexShrink: 0, whiteSpace: "nowrap",
            }}>{resolved.team}</span>
          </>
        )}

        {/* Price */}
        <div className="keeper-row-price" style={{ flex: "0 0 74px", position: "relative" }}>
          <span style={{
            position: "absolute", left: spacing.sm, top: "50%", transform: "translateY(-50%)",
            fontSize: fontSize.caption, color: colors.text.muted, pointerEvents: "none",
          }}>$</span>
          <input
            type="number"
            min="1"
            step="1"
            value={row.price ?? ""}
            aria-label="Keeper price"
            onChange={e => {
              const v = e.target.value;
              onPatch({ price: v === "" ? "" : Math.max(1, Math.floor(Number(v) || 0)) });
            }}
            style={{
              width: "100%", padding: `${spacing.sm}px ${spacing.xs}px ${spacing.sm}px 18px`,
              borderRadius: radius.sm, fontSize: fontSize.caption, fontFamily: "inherit",
              background: colors.bg.deep, color: colors.text.primary,
              border: `1px solid ${colors.border.subtle}`, boxSizing: "border-box",
            }}
          />
        </div>

        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove keeper"
          style={{
            flex: "0 0 auto", padding: `${spacing.sm}px ${spacing.md}px`,
            borderRadius: radius.sm, background: "transparent",
            border: `1px solid ${colors.border.subtle}`, color: colors.text.muted,
            cursor: "pointer", fontSize: fontSize.caption, fontFamily: "inherit", lineHeight: 1.2,
          }}
        >{"×"}</button>
      </div>
      {/* Only the unresolved-name complaint lives below the row now — the pos and
          team moved up onto the player line. Rendered conditionally rather than
          as a reserved-height slot: a resolved row is the steady state, and
          holding an empty line under every one of them just to keep heights
          uniform costs more than the occasional 14px shift. */}
      {bad && (
        <div style={{ fontSize: fontSize.micro, color: "#f87171", marginTop: 3, paddingLeft: 2 }}>
          Not on this year&rsquo;s board &mdash; pick from the list.
        </div>
      )}
    </div>
  );
}

const KeeperEntry = React.memo(function KeeperEntry({
  teamConfigs, setTeamConfigs, settings, userOwner, opponentKeys, canonicalRoom,
  problems = [], forceOpen = false,
}) {
  const [open, setOpen] = useState(false);
  const expanded = open || forceOpen;

  const budget = settings?.budget ?? 200;
  const rosterSize = keeperRosterSize(settings);

  const seatOptions = useMemo(
    () => keeperSeatOptions(teamConfigs, opponentKeys, userOwner, canonicalRoom),
    [teamConfigs, opponentKeys, userOwner, canonicalRoom]
  );
  const seatLabel = useMemo(() => {
    const m = new Map();
    seatOptions.forEach(o => m.set(o.key, o));
    return m;
  }, [seatOptions]);

  // Flat row list, RAW (unsanitized) so a half-typed row is visible and editable.
  // Rows are ordered by the seat picker's own order, so moving a keeper between
  // seats doesn't make the list jump around unpredictably.
  const rows = useMemo(() => {
    const out = [];
    for (const o of seatOptions) {
      const list = teamConfigs?.[o.key]?.keepers;
      if (!Array.isArray(list)) continue;
      list.forEach((k, idx) => out.push({ owner: o.key, idx, name: k?.name ?? "", pos: k?.pos ?? "", price: k?.price ?? "" }));
    }
    // Keepers sitting on a config key that is not a seat this league — they would
    // never be applied, so they must be reachable to fix or delete.
    for (const key of Object.keys(teamConfigs || {})) {
      if (seatLabel.has(key)) continue;
      const list = teamConfigs[key]?.keepers;
      if (!Array.isArray(list) || !list.length) continue;
      list.forEach((k, idx) => out.push({ owner: key, idx, orphan: true, name: k?.name ?? "", pos: k?.pos ?? "", price: k?.price ?? "" }));
    }
    return out;
  }, [teamConfigs, seatOptions, seatLabel]);

  const writeList = (owner, fn) => {
    setTeamConfigs(prev => {
      const cur = Array.isArray(prev[owner]?.keepers) ? prev[owner].keepers : [];
      return { ...prev, [owner]: { ...(prev[owner] || {}), keepers: fn(cur) } };
    });
  };

  const addRow = () => {
    // New rows default to the user's own seat: keeping your own players is the
    // common case, and it is the option the picker lists first.
    writeList(USER_CONFIG_KEY, cur => [...cur, { name: "", pos: "", price: 1 }]);
    setOpen(true);
  };

  const patchRow = (owner, idx, patch) =>
    writeList(owner, cur => cur.map((k, i) => (i === idx ? { ...k, ...patch } : k)));

  const removeRow = (owner, idx) =>
    writeList(owner, cur => cur.filter((_, i) => i !== idx));

  // Moving a keeper between seats is a remove + append in ONE state write, or a
  // dropped frame between the two would show the row twice or not at all.
  const moveRow = (owner, idx, nextOwner) => {
    if (nextOwner === owner) return;
    setTeamConfigs(prev => {
      const from = Array.isArray(prev[owner]?.keepers) ? prev[owner].keepers : [];
      const rec = from[idx];
      if (!rec) return prev;
      const to = Array.isArray(prev[nextOwner]?.keepers) ? prev[nextOwner].keepers : [];
      return {
        ...prev,
        [owner]: { ...(prev[owner] || {}), keepers: from.filter((_, i) => i !== idx) },
        [nextOwner]: { ...(prev[nextOwner] || {}), keepers: [...to, rec] },
      };
    });
  };

  // Per-seat totals, from the SANITIZED records — the same arithmetic the engine
  // will do, so this readout cannot flatter a setup the draft would reject.
  const totals = useMemo(() => {
    const out = [];
    for (const o of seatOptions) {
      const list = sanitizeKeepers(teamConfigs?.[o.key]?.keepers);
      if (!list.length) continue;
      const spend = list.reduce((s, k) => s + k.price, 0);
      const openSlots = rosterSize - list.length;
      out.push({
        key: o.key, label: o.label, isUser: o.isUser, n: list.length, spend,
        left: budget - spend, openSlots,
        // The same rule validateKeepers enforces: every open slot needs $1.
        over: spend > budget - openSlots,
      });
    }
    return out;
  }, [teamConfigs, seatOptions, rosterSize, budget]);

  const count = rows.length;

  return (
    <div style={{ marginTop: spacing.xxl }}>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen(v => !v)}
        style={{
          width: "100%", padding: `${spacing.lg}px`, borderRadius: radius.lg,
          fontSize: fontSize.caption, fontWeight: fontWeight.bold, fontFamily: fonts.display,
          letterSpacing: "0.5px", display: "flex", alignItems: "center", justifyContent: "center",
          gap: spacing.sm,
          background: expanded ? "rgba(255,107,0,0.1)" : colors.bg.surface,
          color: expanded ? colors.brand.accent : colors.text.secondary,
          border: `1px solid ${expanded ? colors.border.brandLight : colors.border.subtle}`,
          cursor: "pointer",
        }}
      >
        <span>{expanded ? "▴" : "▾"} KEEPERS</span>
        {count > 0 && (
          <span style={{
            fontSize: fontSize.micro, fontWeight: fontWeight.heavy,
            padding: "1px 7px", borderRadius: 999,
            background: problems.length ? "rgba(239,68,68,0.18)" : "rgba(255,107,0,0.18)",
            color: problems.length ? "#f87171" : colors.brand.accent,
          }}>{count}</span>
        )}
      </button>

      {expanded && (
        <div style={{
          marginTop: spacing.lg, padding: spacing.xl,
          background: colors.bg.surface, border: `1px solid ${colors.border.subtle}`,
          borderRadius: radius.xl, animation: "fadeInUp 0.2s ease-out",
        }}>
          <p style={{
            fontSize: fontSize.caption, color: colors.text.secondary,
            margin: `0 0 ${spacing.lg}px`, lineHeight: 1.5,
          }}>
            Players already on a roster this year. Each one goes to a specific team at a
            specific price — that team's budget is docked and the player is off the board.
          </p>

          {/* The PLAYER / KEPT BY / PRICE column header is gone with the
              single-line row it belonged to. Against stacked rows it lined up
              with nothing and actively mislabelled the fields. Each control
              carries its own affordance instead — the name input's placeholder,
              the seat select showing the seat, the price's "$" prefix — and all
              three keep their aria-labels for anyone not reading pixels. */}

          {rows.map(row => (
            <div key={`${row.owner}:${row.idx}`}>
              {row.orphan && (
                <div style={{ fontSize: fontSize.micro, color: "#fbbf24", marginBottom: 2 }}>
                  This keeper is on a seat that isn't in a {seatOptions.length}-team league — move it or remove it.
                </div>
              )}
              <KeeperRow
                row={row}
                seatOptions={seatOptions}
                onPatch={patch => patchRow(row.owner, row.idx, patch)}
                onMove={next => moveRow(row.owner, row.idx, next)}
                onRemove={() => removeRow(row.owner, row.idx)}
              />
            </div>
          ))}

          <button
            type="button"
            onClick={addRow}
            style={{
              padding: `${spacing.sm}px ${spacing.lg}px`, borderRadius: radius.sm,
              background: "transparent", border: `1px dashed ${colors.border.brandLight}`,
              color: colors.brand.accent, cursor: "pointer",
              fontSize: fontSize.caption, fontWeight: fontWeight.bold, fontFamily: "inherit",
              marginTop: rows.length ? spacing.xs : 0,
            }}
          >+ Add keeper</button>

          {totals.length > 0 && (
            <div style={{ marginTop: spacing.xl, display: "flex", flexDirection: "column", gap: 3 }}>
              {totals.map(t => (
                <div key={t.key} style={{
                  fontSize: fontSize.caption,
                  color: t.over ? "#f87171" : colors.text.secondary,
                }}>
                  <span style={{
                    fontWeight: fontWeight.bold,
                    color: t.over ? "#f87171" : t.isUser ? colors.brand.accent : colors.text.primary,
                  }}>{t.label}</span>
                  {" — "}{t.n} keeper{t.n === 1 ? "" : "s"}, ${t.spend} of ${budget},
                  {/* "$-30 left" is how a negative reads if you just interpolate
                      it. Say what actually happened instead. */}
                  {t.left < 0
                    ? ` $${Math.abs(t.left)} OVER budget (${t.openSlots} slot${t.openSlots === 1 ? "" : "s"} still to fill)`
                    : ` $${t.left} left for ${t.openSlots} open slot${t.openSlots === 1 ? "" : "s"}`}
                </div>
              ))}
            </div>
          )}

          {/* THE CAVEAT. Deferred items 5-7 stated plainly, in house voice: one
              muted line, no card, no dismiss, no icon. It is the difference
              between a known limitation and a bug report.

              Direction confirmed from the pricing side: bargain keepers remove
              less MONEY than VALUE from the pool, so a real keeper league inflates
              what's left while the v1 sim does not. The sim genuinely runs cheap.
              In deep keeper leagues the effect will exceed "a little" — if that
              gets reported, it is item 6 (keeper-aware inflation) announcing
              itself, not a bug. Only shown when there is actually a keeper to
              caveat. */}
          {rows.length > 0 && (
            <p style={{
              fontSize: fontSize.caption, color: colors.text.muted,
              lineHeight: 1.5, margin: `${spacing.xl}px 0 0`,
            }}>
              Kept players are off the board and their budgets are docked. I don&rsquo;t yet
              re-price the rest of the pool for the money the keepers took out of it &mdash; in a
              deep keeper league expect the remaining studs to come in a little cheap.
            </p>
          )}

          {problems.length > 0 && (
            <div style={{
              marginTop: spacing.xl, padding: spacing.lg, borderRadius: radius.md,
              background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
            }}>
              <div style={{
                fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: "#f87171",
                letterSpacing: "0.5px", marginBottom: spacing.xs,
              }}>FIX BEFORE STARTING</div>
              {problems.map((p, i) => (
                <div key={i} style={{ fontSize: fontSize.caption, color: colors.text.secondary, lineHeight: 1.5 }}>
                  {"• "}{p}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default KeeperEntry;
